import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { PieChart, DollarSign, TrendingUp, AlertCircle, Check } from 'lucide-react';
import '../../styles/design-system.css';

const BudgetSimulator = ({ onClose }) => {
  const [income, setIncome] = useState('');
  const [result, setResult] = useState(null);

  // Regra 50-30-20 adaptada com dízimo
  const calculateBudget = () => {
    const value = parseFloat(income.replace(/\D/g, '')) / 100;
    if (!value || value <= 0) return;

    const budget = {
      dizimo: value * 0.10,           // 10% - Dízimo
      necessidades: value * 0.45,      // 45% - Necessidades (moradia, alimentação, transporte)
      desejos: value * 0.20,           // 20% - Desejos (lazer, compras)
      investimentos: value * 0.15,     // 15% - Investimentos e reserva
      ofertas: value * 0.10,           // 10% - Ofertas e doações
    };

    const categories = [
      { 
        name: 'Dízimo', 
        value: budget.dizimo, 
        percent: 10, 
        color: '#987D9C',
        icon: '⛪',
        tip: 'Honre ao Senhor com as primícias da sua renda'
      },
      { 
        name: 'Necessidades', 
        value: budget.necessidades, 
        percent: 45, 
        color: '#768597',
        icon: '🏠',
        subcategories: [
          { name: 'Moradia', percent: 30 },
          { name: 'Alimentação', percent: 35 },
          { name: 'Transporte', percent: 20 },
          { name: 'Saúde', percent: 15 },
        ],
        tip: 'Inclui moradia, alimentação, transporte e saúde'
      },
      { 
        name: 'Investimentos', 
        value: budget.investimentos, 
        percent: 15, 
        color: '#4CAF50',
        icon: '📈',
        tip: 'Reserva de emergência + investimentos de longo prazo'
      },
      { 
        name: 'Desejos', 
        value: budget.desejos, 
        percent: 20, 
        color: '#BCA182',
        icon: '🎯',
        tip: 'Lazer, compras pessoais e entretenimento'
      },
      { 
        name: 'Ofertas', 
        value: budget.ofertas, 
        percent: 10, 
        color: '#E91E63',
        icon: '❤️',
        tip: 'Ofertas, missões e ajuda ao próximo'
      },
    ];

    setResult({ total: value, categories });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const handleIncomeChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value) {
      value = (parseInt(value) / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      });
    }
    setIncome(value);
  };

  return (
    <div className="p-6 max-h-[80vh] overflow-y-auto">
      <div className="flex items-center gap-3 mb-6">
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: 'var(--accent-blue-200)' }}
        >
          <PieChart size={24} style={{ color: 'var(--text-primary)' }} />
        </div>
        <div>
          <h3 className="heading-2">Simulador de Orçamento Familiar</h3>
          <p className="caption">Baseado em princípios bíblicos de prosperidade</p>
        </div>
      </div>

      {!result ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <label className="body-small block mb-2">Qual sua renda mensal?</label>
          <input
            type="text"
            value={income}
            onChange={handleIncomeChange}
            placeholder="R$ 0,00"
            className="w-full px-4 py-3 rounded-xl text-lg mb-4"
            style={{ 
              background: 'var(--bg-section)', 
              border: '2px solid var(--border-input)',
              outline: 'none'
            }}
          />
          
          <div className="voice-card accent-grey mb-4">
            <p className="body-small">
              💡 <strong>Método utilizado:</strong> Adaptação cristã da regra 50-30-20, 
              priorizando o dízimo e incluindo ofertas como parte essencial do orçamento.
            </p>
          </div>

          <button
            onClick={calculateBudget}
            disabled={!income}
            className="btn-primary w-full"
            style={{ opacity: income ? 1 : 0.5 }}
          >
            Calcular Meu Orçamento
          </button>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {/* Summary */}
          <div className="text-center mb-6 p-4 rounded-xl" style={{ background: 'var(--accent-green-200)' }}>
            <p className="caption mb-1">Renda Total</p>
            <p className="heading-hero">{formatCurrency(result.total)}</p>
          </div>

          {/* Categories */}
          <div className="space-y-3 mb-6">
            {result.categories.map((cat, index) => (
              <motion.div
                key={cat.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="p-4 rounded-xl"
                style={{ background: 'var(--bg-section)' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xl">{cat.icon}</span>
                    <span className="body-medium font-medium">{cat.name}</span>
                  </div>
                  <span className="heading-3">{formatCurrency(cat.value)}</span>
                </div>
                
                {/* Progress bar */}
                <div className="h-2 rounded-full overflow-hidden mb-2" style={{ background: 'var(--border-light)' }}>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${cat.percent}%` }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="h-full rounded-full"
                    style={{ background: cat.color }}
                  />
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="caption">{cat.percent}% da renda</span>
                  <span className="caption" style={{ color: cat.color }}>{cat.tip}</span>
                </div>

                {/* Subcategories */}
                {cat.subcategories && (
                  <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--border-light)' }}>
                    <p className="caption mb-2">Sugestão de distribuição:</p>
                    <div className="grid grid-cols-2 gap-2">
                      {cat.subcategories.map(sub => (
                        <div key={sub.name} className="flex justify-between">
                          <span className="caption">{sub.name}</span>
                          <span className="caption font-medium">
                            {formatCurrency(cat.value * sub.percent / 100)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Tips */}
          <div className="voice-card accent-purple mb-4">
            <div className="flex items-start gap-2">
              <AlertCircle size={18} className="flex-shrink-0 mt-0.5" />
              <div>
                <p className="body-small font-medium mb-1">Princípio Bíblico</p>
                <p className="caption">
                  "Honra ao Senhor com os teus bens e com as primícias de toda a tua renda." - Provérbios 3:9
                </p>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => setResult(null)}
              className="btn-secondary flex-1"
            >
              Recalcular
            </button>
            <button
              onClick={onClose}
              className="btn-primary flex-1"
            >
              Quero Consultoria
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default BudgetSimulator;
